<script setup>
import { defineProps, computed } from 'vue';
import cloakedLogo from '@/assets/images/cloaked-logo.svg';
import moment from 'moment';
import { useMainStore } from '../../store.js';
import { getUTMParams } from '@/utils';

const main = useMainStore();

const props = defineProps({
  accounts: {
    type: Number,
    required: true,
  },
  cta: {
    type: Boolean,
    default: false,
  },
});

const currentDate = moment().format('MM•DD•YYYY');

const iframeUrl = computed(() => {
  return `https://number-scan-widget.cloaked.com/?${getUTMParams()}`;
});

const cloakedUrl = computed(() => {
  return `https://www.cloaked.com/?${getUTMParams()}`;
});

function fbqTrackingLead() {
  if (!window.fbq) return;
  window.fbq('track', 'Lead');
}

</script>

<template>
  <div class="card-container">
    <div
      class="card"
      :class="{
        phew: props.accounts == 0,
        nice: props.accounts >= 1 && props.accounts <= 5,
        alert: props.accounts >= 6 && props.accounts <= 11,
        danger: props.accounts >= 12,
      }"
    >
      <main>
        <div
          v-if="props.accounts >= 12"
          class="text"
        >
          <h1>High!</h1>
          <p />
          <p>
            You are at high risk of identity theft and financial fraud
          </p>
          <div class="get-started-text">
            Get started with 
            <a
              :href="cloakedUrl"
              target="_blank"
              class="external"
              @click="fbqTrackingLead"
            >
              <span><strong>Cloaked</strong></span>

              <span>
                <svg
                  viewBox="0 0 18 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.9922 13.6211C17.9922 14.0664 17.8438 14.4414 17.5469 14.7461C17.2578 15.043 16.9102 15.1914 16.5039 15.1914C16.0898 15.1914 15.7422 15.043 15.4609 14.7461C15.1875 14.4414 15.0508 14.0742 15.0508 13.6445V9.4375L15.2148 5.66406L13.457 7.62109L3.48438 17.6055C3.14844 17.9336 2.77734 18.0977 2.37109 18.0977C2.09766 18.0977 1.84375 18.0234 1.60938 17.875C1.375 17.7266 1.18359 17.5391 1.03516 17.3125C0.894531 17.0781 0.824219 16.8281 0.824219 16.5625C0.824219 16.1562 0.992188 15.7812 1.32812 15.4375L11.3008 5.46484L13.2578 3.73047L9.35547 3.87109H5.27734C4.85547 3.87109 4.49219 3.73047 4.1875 3.44922C3.88281 3.16797 3.73047 2.82422 3.73047 2.41797C3.73047 2.01172 3.87891 1.66406 4.17578 1.375C4.47266 1.07813 4.84766 0.929688 5.30078 0.929688H16.3984C16.8828 0.929688 17.2695 1.07813 17.5586 1.375C17.8555 1.66406 18.0039 2.04688 18.0039 2.52344L17.9922 13.6211Z"
                    fill="#FFFFFF"
                  />
                </svg>
              </span>
              <div class="underline">
                <div class="inner" />
              </div>
            </a>
          </div>
        </div>

        <div
          v-if="props.accounts >= 6 && props.accounts <= 11"
          class="text"
        >
          <h1>Ugh.</h1>
          <p>Get your privacy together, kid.</p>
          <p>
            We found your data on
            <strong>{{ props.accounts }} websites</strong>
          </p>
        </div>

        <div
          v-if="props.accounts >= 1 && props.accounts <= 5"
          class="text"
        >
          <h1>Medium.</h1>
          <p>Your data's popped up {{ props.accounts === 1 ? 'in one place' : 'in a few places' }}. Let's tighten up your digital footprint.</p>
          <p>
            We found your data on
            <strong>{{ props.accounts }} {{ props.accounts === 1 ? 'website' : 'websites' }}</strong>
          </p>
        </div>

        <div
          v-if="props.accounts <= 0"
          class="text"
        >
          <h1>Heads-up!</h1>
          <p />
          <p style="margin-top: 20px;">
            Your email hasn't turned up in a data breach, but your phone number could still be exposed. Scan now
          </p>
        </div>

        <div class="date">
          <span>{{ currentDate }}</span>
        </div>

        <div class="logo">
          <span>Built by</span>
          <cloakedLogo />
        </div>
      </main>

      <div class="background" />
    </div>

    <iframe
      v-if="props.cta && main.breachCount >= 0"
      :src="iframeUrl"
      title="Number Scan Widget"
      class="widget-iframe"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "sass:color";
.card-container {
  width: 100%;

  @media (min-width: 1024px) {
    max-width: 362px;
    order: 2;
    position: sticky;
    top: 20px;
  }
  @media (max-width: 1023px) {
    .cta {
      display: none;
    }
  }
}

.card {
  width: 100%;
  border-radius: 34px;
  background-color: #303539;
  height: 427px;
  // height: 520px;
  // height: 500px;
  // below for mobile sizes
  // height: 480px;
  display: flex;
  padding: 5px;
  color: white;
  position: relative;
  box-shadow: 0px 61.976654052734375px 61.976654052734375px 0px
    #0000001a;

  body.danger &,
  &.danger {
    background-color: #f24141;
  }

  body.alert &,
  &.alert {
    background-color: #fbbc04;
    color: #000;
  }

  body.nice &,
  &.nice {
    background-color: #1a9fcc;
  }

  body.good &,
  &.good {
    background: linear-gradient(
      131.88deg,
      #f64f59 -6.04%,
      #c471ed 44.15%,
      #12c2e9 113.79%
    );
  }
  body.phew &,
  &.phew {
    // new edit
    min-width: 362px;
    background: linear-gradient(
      131.88deg,
      #f64f59 -6.04%,
      #c471ed 44.15%,
      #12c2e9 113.79%
    );

    & + iframe.widget-iframe {
      height: 194.4px;
      border:1px solid black;
      margin-top: 20px;
      border-radius: 30px;
      background: gray;
      width: 100%;

      @media screen and (max-width: 1024px) {
        height: 220px;
      }
      @media screen and (max-width: 767px) {
        height: 251.7px;
      }
      @media screen and (max-width: 517px) {
        height: 208.7px;
      }
      @media screen and (max-width: 417px) {
        height: 194.7px;
      }
    }

    @media screen and (max-width: 380px) {
      display: none;
    }
  }

  main {
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-radius: 30px;
    background-color: inherit;
    position: relative;
    z-index: 2;
    width: 100%;
    padding: 30px 50px 30px 24px;
    overflow: hidden;

    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0.7;
      background-image: url(data:image/png;base64,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);
    }

    .text {
      position: relative;
      z-index: 3;

      .card-icon {
        svg {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          margin-bottom: 10px;
        }
      }

      h1 {
        font-size: 48px;
        font-weight: 600;
        letter-spacing: -0.563px;
        color: inherit;
      }

      h3 {
        font-size: 22px;
        font-weight: 600;
        letter-spacing: -0.563px;
        color: inherit;
      }

      p {
        &:nth-of-type(1) {
          margin-top: 10px;
          font-size: 16.747px;
          font-weight: 500;
          letter-spacing: -0.239px;
          color: inherit;
        }

        &:nth-of-type(2) {
          margin-top: 50px;
          font-size: 28px;
          font-weight: 400;
          letter-spacing: -0.563px;
          color: inherit;
        }
      }

      .get-started-text {
        margin-top: 16px;
        a {
          color: white;
          width: fit-content;
          position: relative;
          svg {
            margin-left: 4px;
            width: 12px;
          }
          .underline {
            position: absolute;
            right: 0;
            height: 2px;
            width: 100%;
            transition: all 0.25s ease-in-out;
            .inner {
              width: 0%;
              height: 2px;
              background-color: white;
              transition: all 0.25s ease-in-out;
            }
          }
          &:hover {
            .underline {
              .inner {
                width: 100%;
                background-color: white;
              }
            }
          }
        }
      }
    }

    .logo {
      position: absolute;
      bottom: 27px;
      left: 26px;

      span {
        display: block;
        margin-bottom: 4px;
        font-size: 14px;
        font-weight: 500;
        line-height: 21px;
        letter-spacing: -0.1;
      }

      svg {
        max-width: 142px;
        height: auto;
        display: block;
        width: 100%;
      }
    }
  }

  .date {
    position: absolute;
    bottom: 32px;
    right: 27px;
    transform: rotate(-90deg);
    transform-origin: 50px -25px;

    span {
      display: block;
      font-size: 17px;
      font-weight: 500;
      letter-spacing: -0.239px;
      color: inherit;
    }
  }

  .background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(
      165.37deg,
      rgba(255, 255, 255, 0.3) 10.35%,
      rgba(0, 0, 0, 0.3) 98.99%
    );
    z-index: 1;
  }
}

.cta {
  padding: 16px 24px 32px 24px;
  border-radius: 16px;
  background: #191e23;
  box-shadow: 0px 27px 40px -22px rgba(0, 0, 0, 0.06);
  margin-top: 15px;
  text-align: center;

  svg {
    display: inline-block;
  }

  h1 {
    color: #fff;
    font-size: 24px;
    font-weight: 600;
    line-height: 24px;
    letter-spacing: -0.5px;
    text-align: center;
  }

  p {
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
    letter-spacing: -0.20000000298023224px;
    text-align: center;
    color: #ffffff;
    margin-top: 10px;
  }

  a {
    display: block;
    background-color: #fff;
    padding: 11px 16px;
    border-radius: 20px;
    margin-top: 24px;
    color: #191e23;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;

    &:hover {
      background-color: color.adjust(#fff, $lightness: -10%);;
    }
  }
}

.widget-iframe {
  width: 100%;
  height: 210px;
  border: 0px;
  margin-top: 20px;
}
</style>
